import { apiGet, apiPost } from '@gat-ship/lib'
import type {
  SearchForStakeholderRequestPayload,
  SearchForStakeholderRequestResponse,
  StakeholderType,
  Stakeholder,
  SearchForStakeholderContactRequestResponse,
  StakeholderContact,
  SearchForStakeholderContactRequestPayload
} from '@gat-ship/types'
import { searchContextItems } from './global-search-utils'
import { icons } from '@gat-ship/icons'
import {
  type AppRoute,
  type GatRouteLocation,
  routeDefinitions
} from '@gat-ship/lib/router'
import type { SearchResult, StakeholderFieldValues } from './search-types'
import type { IconValues } from '@gat-ship/icons'
import { accessCodes } from '@gat-ship/types'
import { type UserService } from '@gat-ship/lib/user'
import { globalUtils } from '@gat-ship/utils'

export interface GetStakeholdersRequestParams {
  /** The type of the stakeholders ('client', 'vendor' or 'other'). */
  stakeholder_type: StakeholderType
  /** Optionally include inactive (soft removed). If omitted, inactive stakeholders are excluded by default. */
  include_inactive: boolean
}

function getStakeholderContextType(stakeholderType: StakeholderType) {
  switch (stakeholderType) {
    case 'client':
      return searchContextItems.client
    case 'vendor':
      return searchContextItems.vendors
    case 'other':
      return searchContextItems.otherParties
    default:
      throw new Error('Invalid stakeholder type.')
  }
}

function getStakeholderIcon(stakeholderType: StakeholderType) {
  switch (stakeholderType) {
    case 'client':
      return icons.stakeholders.client.base
    case 'vendor':
      return icons.stakeholders.vendor.base
    case 'other':
      return icons.stakeholders.other.base
    default:
      throw new Error('Invalid stakeholder type.')
  }
}

function getStakeholderRoute(
  stakeholderType: StakeholderType,
  stakeholderId: string
): GatRouteLocation<AppRoute> {
  switch (stakeholderType) {
    case 'client':
      return {
        name: routeDefinitions.stakeholders.client.general,
        params: { id: stakeholderId }
      }
    case 'vendor':
      return {
        name: routeDefinitions.stakeholders.vendor.general,
        params: { id: stakeholderId }
      }
    case 'other':
      return {
        name: routeDefinitions.stakeholders.other.general,
        params: { id: stakeholderId }
      }
    default:
      throw new Error('Invalid stakeholder type.')
  }
}

function mapStakeholderFields(inputValue: string): StakeholderFieldValues {
  const fields: StakeholderFieldValues = {
    name: {
      field: 'name',
      operator: 'contains',
      type: 'string',
      value: inputValue
    },
    type: {
      field: 'type',
      operator: 'equals',
      type: 'string',
      value: 'vendor'
    }
  }
  return fields
}

function getContactTitle(contact: StakeholderContact): string {
  if (contact.contactType === 'person') {
    return contact.firstName + ' ' + contact.lastName
  }
  return contact.otherName!
}

function getContactRoute(
  contact: StakeholderContact
): GatRouteLocation<AppRoute> | undefined {
  if (contact.stakeholderType === 'client') {
    return {
      name: routeDefinitions.stakeholders.client.contacts,
      params: { id: contact.stakeholderId }
    }
  }
  if (contact.stakeholderType === 'vendor') {
    return {
      name: routeDefinitions.stakeholders.vendor.contacts,
      params: { id: contact.stakeholderId }
    }
  }
  if (contact.stakeholderType === 'other') {
    return {
      name: routeDefinitions.stakeholders.other.contacts,
      params: { id: contact.stakeholderId }
    }
  }
  console.error('Invalid stakeholder type.')
  return undefined
}

export async function contactsSearchRequest(
  params: SearchForStakeholderRequestPayload,
  options?: StakeholdersSearchRequestOptions
): Promise<SearchResult<StakeholderContact>[]> {
  const commandId = globalUtils.functions.uuid()
  const response = await apiPost<
    SearchForStakeholderContactRequestResponse,
    SearchForStakeholderContactRequestPayload
  >(`stakeholders/contacts/search`, params, commandId, {
    signal: options?.signal
  })
  return response.contacts.map((contact) => ({
    id: contact.id,
    title: getContactTitle(contact),
    contextType: searchContextItems.contacts,
    subTitle: contact.email ?? undefined,
    icon:
      contact.contactType === 'person'
        ? icons.stakeholders.contacts.contactPerson
        : icons.stakeholders.contacts.contactPoint,
    metadata: contact,
    actions: {
      selected: (router) => {
        const route = getContactRoute(contact)
        if (route) {
          router.push(route)
        }
      }
    }
  }))
}

/** Gets a list of stakeholders for a given type. */
export interface StakeholdersSearchRequestOptions {
  signal?: AbortSignal
}

export async function stakeholdersSearchRequest(
  params: SearchForStakeholderRequestPayload,
  options?: StakeholdersSearchRequestOptions
): Promise<SearchResult<Stakeholder>[]> {
  const commandId = globalUtils.functions.uuid()
  const response = await apiPost<
    SearchForStakeholderRequestResponse,
    SearchForStakeholderRequestPayload
  >(`stakeholders/search`, params, commandId, {
    signal: options?.signal
  })
  return response.stakeholders.map((stakeholder) => ({
    id: stakeholder.id,
    title: stakeholder.name,
    contextType: getStakeholderContextType(stakeholder.stakeholderType),
    subTitle: stakeholder.name,
    icon: getStakeholderIcon(stakeholder.stakeholderType),
    metadata: stakeholder,
    actions: {
      selected: (router) => {
        router.push(
          getStakeholderRoute(stakeholder.stakeholderType, stakeholder.id)
        )
      }
    }
  }))
}

export async function allStakeholdersSearchRequest(
  params: Omit<SearchForStakeholderRequestPayload, 'stakeholderType'>
): Promise<SearchResult<Stakeholder>[]> {
  const promises: Promise<SearchResult<Stakeholder>[]>[] = []
  promises.push(
    stakeholdersSearchRequest({
      ...params
    })
  )
  promises.push(
    stakeholdersSearchRequest({
      ...params
    })
  )
  promises.push(
    stakeholdersSearchRequest({
      ...params
    })
  )
  const results = await Promise.all(promises)
  return results.flat()
}

export async function getNavigationSearchResults(
  search: string | undefined,
  user: UserService
): Promise<SearchResult[]> {
  if (!search) {
    return []
  }
  console.log('getNavigationSearchResults', user.id.value)
  const result: SearchResult[] = []
  const contextType = searchContextItems.navigation
  const subTitle = 'Navigation'
  const icon = 'Link' satisfies IconValues
  if (user.isTenantAdmin.value) {
    result.push({
      id: 'navigation.tenant-users',
      title: 'Tenant Users',
      contextType: searchContextItems.navigation,
      subTitle: 'Administration • Tenant Users',
      searchValues: ['Tenant', 'Tenants', 'administration'],
      icon,
      actions: {
        selected: (router) => {
          router.push({
            name: routeDefinitions.administration.tenant.tenantUsers
          })
        }
      }
    })
    result.push({
      id: 'navigation.access-roles-list',
      title: 'Access Roles',
      contextType: searchContextItems.navigation,
      subTitle: 'Administration • Access Roles',
      searchValues: ['Access', 'Accesses', 'administration'],
      icon,
      actions: {
        selected: (router) => {
          router.push({
            name: routeDefinitions.administration.accessControl.accessRolesList
          })
        }
      }
    })
    result.push({
      id: 'navigation.companies',
      title: 'Companies',
      contextType: searchContextItems.navigation,
      subTitle: 'Administration • Companies',
      searchValues: ['Company', 'Companies', 'administration'],
      icon,
      actions: {
        selected: (router) => {
          router.push({
            name: routeDefinitions.administration.companies
          })
        }
      }
    })
    result.push({
      id: 'navigation.currency-codes',
      title: 'Currencies',
      contextType: searchContextItems.navigation,
      subTitle: 'Administration • Currencies',
      searchValues: ['Currency', 'Currencies', 'administration'],
      icon,
      actions: {
        selected: (router) => {
          router.push({
            name: routeDefinitions.administration.currencyCodes
          })
        }
      }
    })
  }
  if (user.hasPermission(accessCodes.system.agency.viewAgencyStructure)) {
    result.push({
      id: 'navigation.agency-structure',
      title: 'Agency Structure',
      contextType: searchContextItems.navigation,
      subTitle: 'Administration • Agency Structure',
      searchValues: ['Agency', 'Agencies', 'administration'],
      icon,
      actions: {
        selected: (router) => {
          router.push({
            name: routeDefinitions.administration.agencyStructure,
            query: {}
          })
        }
      }
    })
  }
  return result.filter(
    (item) =>
      item.title.toLowerCase().includes(search.toLowerCase()) ||
      item.searchValues?.some((value) =>
        value.toLowerCase().includes(search.toLowerCase())
      )
  )
}
